import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { RouterLink } from '@angular/router';
import { filter, firstValueFrom, Subscription } from 'rxjs';
import { ApplicationContext, ClientAccessInfoDto } from 'src/app/model';
import { PermissionsEnum } from 'src/app/model/common/permissions.enum';
import { ReturnDueCountRequestDto } from 'src/app/model/returns-due/return-due-request.dto';
import {
  LOCK_TOGGLE_ELIGIBLE_SHEETS,
  WORKPAPER_OPERATION_KIND,
  WORKPAPER_OPERATION_LABELS,
} from 'src/app/constants/workbook.constants';

import {
  ExcelHelperService,
  GlobalStateService,
  HeaderDataService,
  UserContextPermissionRoleService,
  WorkpaperOperationLockService,
} from 'src/app/services';
import { UserContextInitService } from 'src/app/services/user-workpaper-context/user-workpaper-context.service';
import { getMonthName } from 'src/utils';
import { SaveWorksheetService } from 'src/app/services/worksheet/save-worksheet.service';
import { ToggleThemeService } from 'src/app/services/toggle-themes/toggle-theme.service';

@Component({
  selector: 'app-header',
  imports: [CommonModule, RouterLink],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent implements OnDestroy {
  appContext!: ApplicationContext;
  currentPeriod: string = '';
  workpaperInfo: ClientAccessInfoDto | null = null;
  returnsCount: number = 0;
  activeSheetName = '';
  isLockActionInProgress = false;
  isActiveSheetProtected = false;
  canShowLockControl = false;
  hasUnlockWorksheetPermission = false;
  lockControlMessage = '';
  private readonly subscriptions = new Subscription();
  private lockStateRequestId = 0;
  private readonly eligibleSheets = new Set<string>(LOCK_TOGGLE_ELIGIBLE_SHEETS);
  private readonly onWorksheetActivated = async (): Promise<void> => {
    try {
      const sheetName = (await this.excelHelperService.getActiveSheetName())?.trim() ?? '';
      await this.handleActiveSheetChanged(sheetName);
      this.cd.detectChanges();
    } catch (error) {
      console.error('Failed to process worksheet activation event:', error);
    }
  };
  @Input() hasAccess: boolean = false;
  @Input() isEntityLocked: boolean = false;
  @Input() isHomePage: boolean = true;
  @Output() homeClicked = new EventEmitter<void>();

  constructor(
    private globalStateService: GlobalStateService,
    private userContextPermissionRoleService: UserContextPermissionRoleService,
    private headerDataService: HeaderDataService,
    private excelHelperService: ExcelHelperService,
    private userContextInitService: UserContextInitService,
    private cd: ChangeDetectorRef,
    protected readonly saveService: SaveWorksheetService,
    protected readonly themeService: ToggleThemeService,
    protected readonly operationLockService: WorkpaperOperationLockService,
  ) {
    this.appContext = this.globalStateService.getWorkpaperContextData();
  }

  async ngOnInit() {
    await this.loadEntityAndPeriod();
    await this.loadReturnDue();
    this.initializeUnlockWorksheetPermission();
    await this.initializeActiveSheetTracking();
  }

  async loadEntityAndPeriod() {
    this.workpaperInfo = await firstValueFrom(this.userContextPermissionRoleService.loadAccessInfo(this.appContext));
    
    if (this.workpaperInfo) {
      this.currentPeriod =
        `${getMonthName(this.workpaperInfo?.Month)} ${this.workpaperInfo?.Year}` || '';
    }
  }

  async openSutcWeb() {
    if (this.workpaperInfo?.ApplicationUrl) {
      window.open(this.workpaperInfo.ApplicationUrl, '_blank', 'noopener,noreferrer');
    } else {
      console.warn('Application URL not available.');
    }
  }

  async loadReturnDue() {
    const req: ReturnDueCountRequestDto = this.globalStateService.isRGWorkpaper()
      ? {
          clientId: this.appContext.clientId,
          reportingGroupId: this.appContext.reportingGroupId,
          clientTaxPeriodId: +this.appContext.clientTaxPeriodId,
        }
      : {
          clientId: this.appContext.clientId,
          entityId: this.appContext.entityId,
          clientTaxPeriodId: +this.appContext.clientTaxPeriodId,
        };
    const dto = await firstValueFrom(this.headerDataService.loadReturnDue(req));

    const get = (k: string) => dto.Data?.[k] ?? 0;
    this.returnsCount = get('Total');

    if (!dto.Success) {
      console.error('API Error:', dto.Value);
    }
  }

  async onToggleActiveSheetLock(): Promise<void> {
    if (
      !this.hasUnlockWorksheetPermission
      || !this.canShowLockControl
      || this.isEntityLocked
      || this.isLockActionInProgress
      || this.operationLockService.isOperationInProgress()
    ) {
      return;
    }

    const targetSheetName = this.activeSheetName;
    if (!targetSheetName) {
      return;
    }

    try {
      await this.operationLockService.runExclusive(
        {
          kind: WORKPAPER_OPERATION_KIND.SheetLockToggle,
          label: `${WORKPAPER_OPERATION_LABELS.Toggle} ${targetSheetName}`,
          ownerId: targetSheetName,
        },
        () => this.toggleActiveSheetLockInternal(targetSheetName)
      );
    } catch (error) {
      const errorMessage = this.getErrorMessage(error);
      console.error('Failed to toggle sheet protection state:', {
        sheetName: targetSheetName,
        error: errorMessage,
      });
      this.lockControlMessage =
        `Unable to update sheet lock state. ${errorMessage}`;
    }
  }

  private async toggleActiveSheetLockInternal(targetSheetName: string): Promise<void> {
    this.isLockActionInProgress = true;
    this.lockControlMessage = '';

    try {
      await this.ensureWorkpaperPassword();
      const password = this.appContext.workpaperPassword || '';
      const isCurrentlyProtected =
        await this.excelHelperService.getSheetProtectionState(targetSheetName);

      if (isCurrentlyProtected) {
        await this.excelHelperService.unprotectSheet(targetSheetName, password);
      } else {
        await this.excelHelperService.protectSheet(targetSheetName, password);
      }

      try {
        this.isActiveSheetProtected =
          await this.excelHelperService.getSheetProtectionState(targetSheetName);
      } catch {
        // Fallback: when state read fails after successful operation, keep UI consistent.
        this.isActiveSheetProtected = !isCurrentlyProtected;
      }
    } finally {
      this.isLockActionInProgress = false;
    }
  }

  private async handleActiveSheetChanged(sheetName: string): Promise<void> {
    this.activeSheetName = sheetName;
    this.canShowLockControl = this.eligibleSheets.has(sheetName);
    this.lockControlMessage = '';

    if (!this.canShowLockControl) {
      return;
    }

    const requestId = ++this.lockStateRequestId;
    try {
      const isProtected = await this.excelHelperService.getSheetProtectionState(sheetName);
      if (requestId !== this.lockStateRequestId) {
        return;
      }
      this.isActiveSheetProtected = isProtected;
    } catch (error) {
      if (requestId !== this.lockStateRequestId) {
        return;
      }
      this.isActiveSheetProtected = false;
      this.lockControlMessage =
        'Unable to read sheet lock state. Please try again.';
      console.error('Failed to read sheet protection state:', error, {
        sheetName,
      });
    }
  }

  private async ensureWorkpaperPassword(): Promise<void> {
    if (
      !this.appContext?.workpaperPassword ||
      this.appContext.workpaperPassword.length === 0
    ) {
      await this.userContextInitService.setPassword();
      this.appContext = this.globalStateService.getWorkpaperContextData();
    }
  }

  get lockControlIconClass(): string {
    if (this.isLockActionInProgress) {
      return 'ph-arrows-clockwise';
    }

    return this.isActiveSheetProtected ? 'ph-lock' : 'ph-lock-open';
  }

  get isLockControlDisabled(): boolean {
    return this.isEntityLocked || this.isLockActionInProgress || this.operationLockService.isOperationInProgress();
  }

  get isSaveDisabled(): boolean {
    return this.saveService.isSaving() || this.operationLockService.isOperationInProgress();
  }

  get lockControlTitle(): string {
    const sheetName = this.activeSheetName || 'active';

    if (this.isEntityLocked) {
      return 'Lock controls are disabled because this entity is locked.';
    }

    if (this.isActiveSheetProtected) {
      return `Unlock ${sheetName}`;
    }

    return `Lock ${sheetName}`;
  }

  get canRenderLockControl(): boolean {
    return this.hasUnlockWorksheetPermission && this.canShowLockControl;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    void this.unregisterWorksheetActivationHandler();
  }

  private initializeUnlockWorksheetPermission(): void {
    this.subscriptions.add(
      this.userContextInitService.userContext$
        .pipe(filter((context) => !!context))
        .subscribe({
          next: (context) => {
            this.hasUnlockWorksheetPermission =
              !!context?.permissions?.includes(PermissionsEnum.unlockWorksheet);
            this.cd.markForCheck();
          },
          error: (error) => {
            console.error('Error loading unlock worksheet permission:', error);
          },
        })
    );
  }

  private getErrorMessage(error: unknown): string {
    if (error instanceof Error && error.message) {
      return error.message;
    }

    if (typeof error === 'string') {
      return error;
    }

    return 'Please try again.';
  }

  private async initializeActiveSheetTracking(): Promise<void> {
    await this.syncCurrentActiveSheetState();
    await this.registerWorksheetActivationHandler();
  }

  private async syncCurrentActiveSheetState(): Promise<void> {
    try {
      const sheetName = (await this.excelHelperService.getActiveSheetName())?.trim() ?? '';
      await this.handleActiveSheetChanged(sheetName);
      this.cd.detectChanges();
    } catch (error) {
      console.error('Failed to sync current active sheet for lock control:', error);
    }
  }

  private async registerWorksheetActivationHandler(): Promise<void> {
    if (typeof Excel === 'undefined' || typeof Excel.run !== 'function') {
      return;
    }

    try {
      await Excel.run(async (context) => {
        context.workbook.worksheets.onActivated.add(this.onWorksheetActivated);
        await context.sync();
      });
    } catch (error) {
      console.error('Failed to register worksheet activation handler:', error);
    }
  }

  private async unregisterWorksheetActivationHandler(): Promise<void> {
    if (typeof Excel === 'undefined' || typeof Excel.run !== 'function') {
      return;
    }

    try {
      await Excel.run(async (context) => {
        context.workbook.worksheets.onActivated.remove(this.onWorksheetActivated);
        await context.sync();
      });
    } catch {
      // Ignore cleanup failures.
    }
  }
}
